import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'What Is Ethereum?',
    tagline: 'A shared, programmable computer that anyone can verify.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - what makes Ethereum different from a single-purpose ledger like Bitcoin
//   Q1 - what "state" means on Ethereum
//   Q2 - what a full node actually does when it processes a block
export const blocks: StageBlock[] = [
    content('hero', () => {
        const colors = useColors();
        return (
            <StageSection gradientBorder eyebrow="Stage 1 of 8">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: 2 }}>
                    {['World computer', 'Public state', 'Open participation'].map((label) => (
                        <Chip
                            key={label}
                            label={label}
                            size="small"
                            variant="outlined"
                            sx={{ color: colors.accent.cyan, borderColor: colors.border.accent, backgroundColor: colors.accent.cyanGlow, fontFamily: '"Orbitron", sans-serif' }}
                        />
                    ))}
                </Box>
                <Typography variant="h2" sx={{
                    fontFamily: '"Orbitron", sans-serif',
                    fontSize: { xs: '1.9rem', md: '2.6rem' },
                    lineHeight: 1.1,
                    fontWeight: 800,
                    color: colors.text.primary,
                    mb: 2,
                }}>
                    Ethereum is a computer that no single party owns.
                </Typography>
                <Prose>
                    Bitcoin proved that a network of strangers could agree on who owns what, without a bank in the
                    middle. Ethereum, proposed by Vitalik Buterin in a 2013 whitepaper and launched in 2015, asks a
                    bigger question: what if that same trick worked for <em>arbitrary programs</em>, not just a
                    ledger of coin transfers?
                </Prose>
                <Prose>
                    Every participant who runs an Ethereum node keeps a full copy of the network's current state and
                    independently re-executes every transaction to check it&apos;s valid. Nobody has to trust a
                    central server, because everybody can verify the result themselves.
                </Prose>
            </StageSection>
        );
    }),

    content('bitcoin-vs-ethereum', () => (
        <StageSection title="One ledger vs. one computer">
            <Prose>
                Bitcoin's scripting language is deliberately narrow: it can express "who is allowed to spend this
                output," and not much else. Ethereum's scripting language, the EVM, is general-purpose. It can
                express loans, exchanges, voting systems, games, or anything else you can compile down to EVM
                bytecode. That generality is the entire reason "smart contracts" and, later, this platform's
                security challenges exist at all.
            </Prose>
            <FactGrid items={[
                ['Bitcoin', 'A distributed ledger purpose-built for one asset: transferring BTC between addresses.'],
                ['Ethereum', 'A distributed computer: it moves ETH, but it also runs arbitrary programs that can hold and move any asset built on top of it.'],
            ]} />
        </StageSection>
    )),

    question(0),

    content('state-machine', () => (
        <StageSection title="Think in state, not in files">
            <Prose>
                Ethereum's entire job is maintaining one global piece of data called <strong>state</strong>: every
                account's ETH balance, every contract's stored variables, every contract's deployed bytecode, and a
                nonce for each account that counts how many transactions it has sent. A block is nothing more than
                an ordered batch of transactions; applying that batch deterministically turns the previous state
                into the next state.
            </Prose>
            <Prose>
                This is why Ethereum documentation calls it a <strong>state machine</strong>. There's no hidden
                database behind the scenes deciding what "really" happened; the state <em>is</em> the record, and
                it's identical on every honest node.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('nodes', () => (
        <StageSection title="Who actually runs this thing?">
            <Prose>
                Nobody runs "Ethereum" as a company runs a server. Thousands of independently operated nodes each
                download every block, re-execute every transaction against their own copy of state, and reject
                anything that doesn't follow the rules. If your node's resulting state ever disagrees with the
                network's, it's your node that's wrong. There is no admin override.
            </Prose>
            <KeyPoints items={[
                <>A <strong>full node</strong> stores and validates the whole chain, giving it an independent view of the truth.</>,
                <>Wallets and most apps don't run their own node; they ask an existing node's JSON-RPC endpoint, which is why <strong>trusting your RPC provider</strong> is its own security question (more in Stage 4).</>,
                <>Because validation is deterministic, two honest nodes fed the same blocks always reach the same state.</>,
            ]} />
        </StageSection>
    )),

    question(2),

    content('why-it-matters', () => (
        <StageSection title="Why this matters before you start hunting bugs">
            <Prose>
                Every challenge on this platform lives inside that shared, verifiable state. A contract can't
                secretly special-case an admin's transaction the way a normal backend could quietly special-case a
                database row; the code that runs is exactly the bytecode deployed on-chain, and it runs the same
                way for everyone. When a contract has a bug, the network doesn't protect anyone from it; it enforces
                the bug just as faithfully as it enforces the intended behavior. That's the mindset the rest of this
                track builds toward.
            </Prose>
        </StageSection>
    )),
];
